import { faPencil } from '@fortawesome/pro-light-svg-icons/faPencil';
import { faShare } from '@fortawesome/pro-light-svg-icons/faShare';
import { faPrint } from '@fortawesome/pro-regular-svg-icons/faPrint';
import { Note } from '@propertypal/shared/src/types/notes';
import { Property } from '@propertypal/shared/src/types/property';
import Link from 'next/link';
import router from 'next/router';
import React, { CSSProperties, FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import Chevron from '../icons/Chevron';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import PdfDropdown from '../pdf/PdfDropdown';
import OpenViewingOverlay from '../property-box/OpenViewingOverlay';
import PropertyBadge from '../property-box/PropertyBadge';
import PropertyFavourite from '../property-box/PropertyFavourite';
import ShowHomesBox from '../property-box/ShowHomesBox';
import { Heading, Text, TextLink } from '../typography';
import PropertyPrice from './PropertyPrice';
import { Container, SummaryOptions, SummaryRow, SummaryText } from './PropertySummary.style';

interface Props {
  containerStyle?: CSSProperties;
  property: Property;
  favourited: boolean;
  toggleFavourited: (favourited: boolean) => Promise<boolean>;
  onShare: () => void;
  loggedIn: boolean;
  notes: Note[];
}

const PropertySummary: FunctionComponent<Props> = (props) => {
  const theme = useTheme();

  const openNotes = () => {
    if (!props.loggedIn) {
      router.push(`/login?sourcePath=${props.property.path}/notes`);
    } else {
      router.replace(`${props.property.path}/notes`, undefined, { shallow: true });
    }
  };

  return (
    <Container style={props.containerStyle}>
      <SummaryRow>
        <div>
          <PropertyBadge property={props.property} disablePlaceholder containerStyle={{ marginBottom: 10 }} />
          <PropertyPrice
            fontSize={40}
            price={props.property.price}
            alternativeCurrencyPrice={props.property.alternativeCurrencyPrice}
            status={props.property.status}
            comingSoonText={props.property.comingSoonText}
            statusButton
            prefixBreak
          />

          {props.property.includeFeeCharges && (
            <p style={{ marginTop: 10 }}>
              <TextLink
                href={`/letting-agent-fees/${props.property.account.accountNumber}`}
                prefetch={false}
                data-testid="fees-link"
                fontSize={14}
              >
                {' '}
                (some fees may apply)
              </TextLink>
            </p>
          )}
        </div>

        <SummaryOptions>
          {props.property.type === 'property' && (
            <PropertyFavourite
              containerStyle={{ marginLeft: -7 }}
              favourited={props.favourited}
              onFavourite={props.toggleFavourited}
              showLabel
              size={22}
            />
          )}

          {props.property.published && props.property.type === 'property' && (
            <>
              <button type="button" onClick={props.onShare} data-testid="sharePropertyButton">
                <FontAwesomeIcon icon={faShare} size={20} />
                Share
              </button>

              <button type="button" onClick={openNotes}>
                <FontAwesomeIcon icon={faPencil} size={20} />
                {props.notes.length ? 'Edit' : 'Add'} Note
              </button>
            </>
          )}

          {!!props.property.brochures?.length && <PdfDropdown property={props.property} />}

          {props.property.type === 'property' && (
            <a href={`/property-print?property=${props.property.id}`} target="_blank" rel="noreferrer">
              <FontAwesomeIcon icon={faPrint} size={20} />
              Print
            </a>
          )}
        </SummaryOptions>
      </SummaryRow>

      <Heading fontWeight="600" data-testid="address-line-1">
        {props.property.displayAddressLine1}
      </Heading>

      <Text fontSize={20} color={theme.textLight} data-testid="address-line-2">
        {props.property.displayAddressLine2}
      </Text>

      {props.property.briefText && (
        <Text fontSize={16} color={theme.textLight} mt={20} data-testid="property-brief-text">
          {props.property.briefText}
        </Text>
      )}

      {props.property.summary && (
        <SummaryText
          html={props.property.summary}
          testId="property-summary-html"
          $hasBriefText={!!props.property.briefText}
        />
      )}

      {props.property.development && (
        <Text fontSize={16} mt={15} color={theme.primary}>
          <Link
            href={props.property.development.path}
            style={{ textDecoration: 'underline' }}
            className="pp-primary-text"
          >
            This property forms part of the <b>{props.property.development.name}</b> development <Chevron size={9} />
          </Link>
        </Text>
      )}

      {props.property.openViewing && (
        <OpenViewingOverlay
          containerStyle={{ marginTop: 30 }}
          openViewing={props.property.openViewing}
          themeName="light"
        />
      )}

      <ShowHomesBox property={props.property} containerStyle={{ marginTop: 30 }} />
    </Container>
  );
};

export default PropertySummary;
